import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { 
  getUsers, 
  deleteUser, 
  getWeddings,
  assignWeddingToUser,
  removeWeddingFromUser,
  updateUser
} from '../../firebase/firestore';
import { createUser } from '../../firebase/auth';
import { getAuth } from 'firebase/auth';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { collection, getDocs, doc, deleteDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const auth = getAuth();

const UsersList = () => {
  const { t } = useTranslation();
  const { userData, currentUser } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    isAdmin: false
  });
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Wedding assignment dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [userToAssign, setUserToAssign] = useState(null);
  const [selectedWedding, setSelectedWedding] = useState('');
  
  // Wedding removal dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [weddingToRemove, setWeddingToRemove] = useState('');

  // Share wedding dialog states
  const [shareDialog, setShareDialog] = useState(false);
  const [shareUserId, setShareUserId] = useState(null);
  const [weddingId, setWeddingId] = useState('');

  // New user dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if current user is admin
        if (!userData?.isAdmin) {
          setError(t('common.unauthorized'));
          setLoading(false);
          return;
        }
        
        // Fetch users
        const usersData = await getUsers();
        setUsers(usersData);
        
        // Fetch weddings
        const weddingsData = await getWeddings(userData.uid, true);
        setWeddings(weddingsData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userData, t]);

  // Handle user dialog
  const handleOpenUserDialog = () => {
    setUserFormData({
      name: '',
      email: '',
      password: '',
      isAdmin: false
    });
    setUserDialogOpen(true);
  };

  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
  };

  const handleUserFormChange = (e) => {
    const { name, value, checked } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: name === 'isAdmin' ? checked : value
    }));
  };

  const handleUserSubmit = async () => {
    try {
      // Create new user
      const { user, error } = await createUser(
        userFormData.name,
        userFormData.email,
        userFormData.password,
        userFormData.isAdmin,
        userData
      );
      
      if (error) {
        setError(error);
        return;
      }
      
      if (user) {
        // Add new user to the list
        const newUser = {
          id: user.uid,
          name: userFormData.name,
          email: userFormData.email,
          isAdmin: userFormData.isAdmin,
          assignedWeddings: []
        };
        
        setUsers([...users, newUser]);
        setSuccess(t('users.createSuccess'));
        handleCloseUserDialog();
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError(t('common.error'));
    }
  };

  // Handle delete dialog
  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await deleteUser(userToDelete.id);
      setUsers(users.filter(user => user.id !== userToDelete.id));
      setSuccess(t('users.deleteSuccess'));
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(t('common.error'));
    }
  };

  // Handle wedding assignment dialog
  const handleOpenAssignDialog = (user) => {
    setUserToAssign(user);
    setSelectedWedding('');
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setUserToAssign(null);
    setSelectedWedding('');
  };

  const handleAssignWedding = async () => {
    if (!userToAssign || !selectedWedding) return;
    
    try {
      await assignWeddingToUser(selectedWedding, userToAssign.id);
      
      // Update local state
      const updatedUsers = users.map(user => {
        if (user.id === userToAssign.id) {
          const assignedWeddings = user.assignedWeddings || [];
          if (!assignedWeddings.includes(selectedWedding)) {
            return {
              ...user,
              assignedWeddings: [...assignedWeddings, selectedWedding]
            };
          }
        }
        return user;
      });
      
      setUsers(updatedUsers);
      setSuccess(t('users.assignSuccess'));
      handleCloseAssignDialog();
    } catch (err) {
      console.error('Error assigning wedding:', err);
      setError(t('common.error'));
    }
  };

  // Handle wedding removal dialog
  const handleOpenRemoveDialog = (user, weddingId) => {
    setUserToRemove(user);
    setWeddingToRemove(weddingId);
    setRemoveDialogOpen(true);
  };

  const handleCloseRemoveDialog = () => {
    setRemoveDialogOpen(false);
    setUserToRemove(null);
    setWeddingToRemove('');
  };

  const handleRemoveWedding = async () => {
    if (!userToRemove || !weddingToRemove) return;
    
    try {
      await removeWeddingFromUser(weddingToRemove, userToRemove.id);
      
      // Update local state
      const updatedUsers = users.map(user => {
        if (user.id === userToRemove.id) {
          return {
            ...user,
            assignedWeddings: (user.assignedWeddings || []).filter(id => id !== weddingToRemove)
          };
        }
        return user;
      });
      
      setUsers(updatedUsers);
      setSuccess(t('users.removeSuccess'));
      handleCloseRemoveDialog();
    } catch (err) {
      console.error('Error removing wedding:', err);
      setError(t('common.error'));
    }
  };

  // Get wedding name by ID
  const getWeddingName = (weddingId) => {
    const wedding = weddings.find(w => w.id === weddingId);
    return wedding ? wedding.name : weddingId;
  };

  // Filter out weddings that are already assigned to the user
  const getAvailableWeddings = (user) => {
    const assignedWeddings = user?.assignedWeddings || [];
    return weddings.filter(wedding => !assignedWeddings.includes(wedding.id));
  };

  const handleOpenShareDialog = (userId) => {
    setShareUserId(userId);
    setShareDialog(true);
  };

  const handleShareWedding = async () => {
    try {
      const user = users.find(u => u.id === shareUserId);
      if (!user) throw new Error('User not found');

      // Add wedding to user's weddings array
      const updatedWeddings = [...(user.assignedWeddings || []), weddingId];
      await updateUser(shareUserId, { assignedWeddings: updatedWeddings });

      setSuccess(t('users.weddingShared'));
      setShareDialog(false);
      setWeddingId('');
      fetchUsers();
    } catch (err) {
      console.error('Error sharing wedding:', err);
      setError(t('users.shareError'));
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserFormData({
        name: user.name || '',
        email: user.email,
        password: '',
        isAdmin: user.isAdmin || false
      });
    } else {
      setEditingUser(null);
      setUserFormData({
        name: '',
        email: '',
        password: '',
        isAdmin: false
      });
    }
    setUserDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setUserDialogOpen(false);
    setEditingUser(null);
    setUserFormData({
      name: '',
      email: '',
      password: '',
      isAdmin: false
    });
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: name === 'isAdmin' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        // Update existing user
        await updateDoc(doc(db, 'users', editingUser.id), {
          name: userFormData.name,
          email: userFormData.email,
          isAdmin: userFormData.isAdmin
        });
        setSuccess(t('users.updateSuccess'));
      } else {
        // Create new user
        const userCredential = await createUser(
          userFormData.name,
          userFormData.email,
          userFormData.password,
          userFormData.isAdmin,
          userData
        );
        
        // Add user data to Firestore
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          name: userFormData.name,
          email: userFormData.email,
          isAdmin: userFormData.isAdmin,
          createdAt: new Date()
        });
        setSuccess(t('users.createSuccess'));
      }
      
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      console.error('Error managing user:', err);
      setError(editingUser ? t('users.updateError') : t('users.createError'));
    }
  };

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('users.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userData?.isAdmin) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {t('common.unauthorized')}
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {t('users.title')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t('users.create')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {users.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('users.noUsers')}
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('users.name')}</TableCell>
                  <TableCell>{t('users.email')}</TableCell>
                  <TableCell>{t('users.isAdmin')}</TableCell>
                  <TableCell>{t('users.assignedWeddings')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.isAdmin ? (
                        <Chip label={t('common.yes')} color="primary" size="small" />
                      ) : (
                        <Chip label={t('common.no')} variant="outlined" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {user.assignedWeddings?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {user.assignedWeddings.map(weddingId => (
                            <Chip
                              key={weddingId}
                              label={getWeddingName(weddingId)}
                              size="small"
                              onDelete={() => handleOpenRemoveDialog(user, weddingId)}
                              deleteIcon={<LinkOffIcon />}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('dashboard.noWeddings')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        color="primary" 
                        onClick={() => handleOpenDialog(user)}
                        title={t('users.edit')}
                        disabled={user.id === currentUser?.uid}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleOpenDeleteDialog(user)}
                        title={t('common.delete')}
                        disabled={user.id === currentUser?.uid}
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton 
                        color="secondary" 
                        onClick={() => handleOpenShareDialog(user.id)}
                        title={t('users.shareWedding')}
                      >
                        <ShareIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create User Dialog */}
        <Dialog open={userDialogOpen} onClose={handleCloseDialog}>
          <DialogTitle>{t('users.create')}</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label={t('users.name')}
                name="name"
                type="text"
                value={userFormData.name}
                onChange={handleChange}
                disabled={editingUser}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label={t('users.email')}
                name="email"
                type="email"
                value={userFormData.email}
                onChange={handleChange}
                disabled={editingUser}
              />
              {!editingUser && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t('users.password')}
                  name="password"
                  type="password"
                  value={userFormData.password}
                  onChange={handleChange}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={userFormData.isAdmin}
                    onChange={handleChange}
                    name="isAdmin"
                  />
                }
                label={t('users.isAdmin')}
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
            <Button 
              onClick={handleSubmit} 
              color="primary"
              disabled={!userFormData.name || !userFormData.email || (!editingUser && !userFormData.password)}
            >
              {editingUser ? t('common.save') : t('common.create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>{t('common.confirmAction')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('users.confirmDelete')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>{t('common.cancel')}</Button>
            <Button onClick={handleDeleteUser} color="error">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Wedding Dialog */}
        <Dialog open={assignDialogOpen} onClose={handleCloseAssignDialog}>
          <DialogTitle>{t('users.assignWedding')}</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="wedding-select-label">{t('weddings.title')}</InputLabel>
              <Select
                labelId="wedding-select-label"
                id="wedding-select"
                value={selectedWedding}
                label={t('weddings.title')}
                onChange={(e) => setSelectedWedding(e.target.value)}
              >
                {getAvailableWeddings(userToAssign).map((wedding) => (
                  <MenuItem key={wedding.id} value={wedding.id}>
                    {wedding.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignDialog}>{t('common.cancel')}</Button>
            <Button 
              onClick={handleAssignWedding} 
              color="primary"
              disabled={!selectedWedding}
            >
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove Wedding Dialog */}
        <Dialog open={removeDialogOpen} onClose={handleCloseRemoveDialog}>
          <DialogTitle>{t('users.removeWedding')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('users.confirmRemoveWedding', { 
                wedding: getWeddingName(weddingToRemove),
                user: userToRemove?.name 
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRemoveDialog}>{t('common.cancel')}</Button>
            <Button onClick={handleRemoveWedding} color="error">
              {t('common.remove')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Share Wedding Dialog */}
        <Dialog open={shareDialog} onClose={() => setShareDialog(false)}>
          <DialogTitle>{t('users.shareWedding')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label={t('users.weddingId')}
              value={weddingId}
              onChange={(e) => setWeddingId(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShareDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleShareWedding} variant="contained">
              {t('common.share')}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default UsersList;
